import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';

import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {selectActiveBranchId} from '@dms/api/features';
import {
  SalesLeaguePeriod,
  useGetSalesLeagueLeaderboardQuery,
  useLazyExportSalesLeagueLeaderboardCsvQuery,
} from '@dms/api/sales';
import {testIds} from '@dms/routes';
import {handleApiError, Main} from '@dms/shared';

import {useQueryState} from 'shared/web';

import {
  SALES_LEAGUE_BRANCH_ID_QUERY_PARAM,
  UNRESOLVED_BRANCH_ID,
} from '../../constants/SALES_LEAGUE_BRANCH_ID_QUERY_PARAM';
import {SALES_LEAGUE_PERIOD_QUERY_PARAM} from '../../constants/SALES_LEAGUE_PERIODS';
import {downloadSalesLeagueCsv} from '../../utils/downloadSalesLeagueCsv';
import {parseSalesLeaguePeriod} from '../../utils/parseSalesLeaguePeriod';
import {SalesLeagueBoard} from './components/SalesLeagueBoard';

const PAGE_MIN_HEIGHT = 100;

export function SalesLeague() {
  // The period lives in the URL so a board can be linked and shared.
  const [rawPeriod, setRawPeriod] = useQueryState(SALES_LEAGUE_PERIOD_QUERY_PARAM, true);
  const period = parseSalesLeaguePeriod(rawPeriod);

  // Present when drilled in from the branch board; otherwise the viewer's active branch.
  const [drillDownBranchId] = useQueryState(SALES_LEAGUE_BRANCH_ID_QUERY_PARAM, true);
  const activeBranchId = useSelector(selectActiveBranchId);

  const resolvedActiveBranchId =
    isNotNil(activeBranchId) && activeBranchId !== UNRESOLVED_BRANCH_ID
      ? activeBranchId
      : undefined;
  const branchId = drillDownBranchId ?? resolvedActiveBranchId;

  // Sending the branch explicitly puts it in the RTK cache key, so switching the active
  // branch in the top bar refetches rather than leaving a stale board on screen.
  const {data, isLoading, isError} = useGetSalesLeagueLeaderboardQuery(
    isNotNil(branchId) ? {period, branchId} : skipToken
  );

  const [exportCsv, {isFetching: isExporting}] = useLazyExportSalesLeagueLeaderboardCsvQuery();

  const handlePeriodChange = (nextPeriod: SalesLeaguePeriod) => setRawPeriod(nextPeriod);

  // The export re-asks the backend for this board, so it takes the same period and branch
  // the query above did — a file for a board the viewer is not looking at would be a bug.
  const handleExport = () => {
    if (isNil(branchId)) {
      return;
    }

    exportCsv({period, branchId}).unwrap().then(downloadSalesLeagueCsv).catch(handleApiError);
  };

  return (
    <Main data-testid={testIds.salesLeague.overview('page')}>
      {/* Loading is rendered as a skeleton board rather than a spinner, so the period
          switcher stays usable and the layout does not jump when the data lands. */}
      <DataStatus isError={isError} minHeight={PAGE_MIN_HEIGHT}>
        <SalesLeagueBoard
          leaderboard={data}
          isLoading={isLoading || isNil(branchId)}
          period={period}
          onPeriodChange={handlePeriodChange}
          onExport={handleExport}
          isExporting={isExporting}
          data-testid={testIds.salesLeague.overview('board')}
        />
      </DataStatus>
    </Main>
  );
}
